import Button from "components/common/Button";
import Colors from "assets/Colors";
import Input from "components/common/Input";
import * as Yup from "yup";
import { useFormik } from "formik";
import DeleteOutlined from "components/icons/DeleteOutlined";
import CloseIconSmall from "components/icons/CloseIconSmall";
import { useTranslation } from "react-i18next";
import s from "./styles";
import usePriceLists from "graphql/usePriceLists";
import CustomSelector from "../Selector";
import CustomCheckbox from "../Checkbox";
import { useMemo, useState } from "react";
import useRoomMutations from "graphql/useRoomMutations";
import { BSON } from "realm-web";

function AddRoomModal({
  close,
  setCurrentRoom,
  room,
  rooms,
  store,
  handleDeleteRoom,
  setErrorMessage,
  onUpdateRoomSuccess,
  onAddRoomSuccess,
}) {
  const { t } = useTranslation();

  const {addRoom, updateRoom} = useRoomMutations();

  const { priceLists, loading: loading1 } = usePriceLists();

  const priceListsOptions = priceLists.map((priceList) => ({
    name: priceList.name,
    value: priceList._id,
  }));

  priceListsOptions.unshift({
    name: t("components.Rooms.AddRoomModal.defaultPriceList"),
    value: null,
  });

  const isVirtualRoomPresent = useMemo(() => {
    return rooms.find((room) => room.virtual);
  }, [rooms]);


  const [loading, setLoading] = useState(false);


  const {
    handleSubmit,
    getFieldProps,
    setFieldValue,
    values,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      name: room?.name || undefined,
      firstPriceList: room?.priceLists?.[0] || undefined,
      secondPriceList: room?.priceLists?.[1] || undefined,
      virtual: room?.virtual || false,
    },

    enableReinitialize: true,

    onSubmit: async (values) => {

     const {firstPriceList, secondPriceList, ...newValues } = values;

     const priceLists = []

      if(firstPriceList){
        priceLists.push(firstPriceList)
      }  
      if(secondPriceList){
        priceLists.push(secondPriceList)
      }

      const roomValues = {
        ...newValues,
        _id: room?._id || new BSON.ObjectId(),
        storeId: new BSON.ObjectId(store),
        priceLists,
        width:"100",
        height:"100",
        updateDatetime: new Date(),
        creationDatetime: new Date(),
      };

      setLoading(true);

      try {
        if (room) {
          updateRoom(room, roomValues).then(() => {
            setLoading(false);
            onUpdateRoomSuccess();
            resetForm();
            close();
          });
        } else {
          addRoom(roomValues).then((data) => {
            setCurrentRoom(data);
            setLoading(false);
            onAddRoomSuccess();
            resetForm();
            close();
          });
        }
      } catch (error) {
        setErrorMessage({ error });
      }
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().required(t("formValidations.required")),
    }),
  });


  return (
    <s.Container>
      <s.HeaderContainer>
        <s.CloseButtonWrapper
          onClick={() => {
             resetForm();
            close();
          }}
        >
          <CloseIconSmall />
        </s.CloseButtonWrapper>
        <div>
          {
            room ? t("components.Rooms.AddRoomModal.editRoom") : t("components.Rooms.AddRoomModal.addRoom")
          }
          
       </div>

        {room ? (
          <Button  
            type="text"
            style={{
              width: "40px",
              height: "40px",
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => handleDeleteRoom(room)}
            icon={<DeleteOutlined size="18px" color={Colors.tertiary} />}
          ></Button>
        ) : (
          <>&nbsp;</>
        )}
      </s.HeaderContainer>
      <s.BodyWrapper>
        <s.SubTitle>
          {t("components.Rooms.AddRoomModal.fields.name.label")}
        </s.SubTitle>
        <Input
          style={{
            marginBottom: "10px",
          }}
          placeholder={t(
            "components.Rooms.AddRoomModal.fields.name.placeholder"
          )}
          name="name"
          {...getFieldProps("name")}
        />
        <CustomCheckbox
          checked={values.virtual}
          disabled={isVirtualRoomPresent && room?.virtual !== isVirtualRoomPresent.virtual}
          onChange={(e) => {
            setFieldValue("virtual", e.target.checked);
          }}
        >
          <s.SubTitle
            style={{
              marginBottom: "20px",
            }}
          >
            {t("components.Rooms.AddRoomModal.fields.virtualRoom.ms1")}
            <s.textSpan>
              {" "}
              {t("components.Rooms.AddRoomModal.fields.virtualRoom.ms2")}
            </s.textSpan>
          </s.SubTitle>
        </CustomCheckbox>
        <s.SubTitle>
          {t("components.Rooms.AddRoomModal.fields.firstPriceList.label")}
          <s.textSpan>
            {t("components.Rooms.AddRoomModal.fields.firstPriceList.span")}
          </s.textSpan>
        </s.SubTitle>
        <CustomSelector
          disabled={loading1}
          placeholder={t(
            "components.Rooms.AddRoomModal.fields.firstPriceList.placeholder"
          )}
          style={{ width: "100%", borderRadius: "2px", marginBottom: "20px" }}
          options={priceListsOptions}
          value={values.firstPriceList}
          onChange={(value) => {
            setFieldValue("firstPriceList", value);
          }}
        />
        <s.SubTitle>
          {t("components.Rooms.AddRoomModal.fields.secondPriceList.label")}
        </s.SubTitle>
        <CustomSelector
          disabled={loading1}
          placeholder={t(
            "components.Rooms.AddRoomModal.fields.secondPriceList.placeholder"
          )}
          style={{ width: "100%", borderRadius: "2px", marginBottom: "20px" }}
          options={priceListsOptions}
          value={values.secondPriceList}
          onChange={(value) => {
            setFieldValue("secondPriceList", value);
          }}
        />
      </s.BodyWrapper>
      <s.containerButtons>
        <Button
          type="primary"
          style={{
            background: "transparent",
            color: Colors.text_primary,
            borderColor: Colors.text_primary,
            width: "200px",
            fontWeight: "500",
            height: "40px",
          }}
           onClick={() => {
             resetForm();
            close();
           }}
        >
          {t("components.Rooms.AddRoomModal.cancel")}
        </Button>
        <Button
          disabled={!isValid || !dirty}
          loading={loading}
          type="primary"
          style={{
            background: Colors.primary,
            color: Colors.text_on_secondary,
            borderColor: Colors.text_primary,
            width: "200px",
            fontWeight: "500",
            height: "40px",
          }}
          onClick={() => handleSubmit()}
        >
          {t("components.Rooms.AddRoomModal.saveRoom")}
        </Button>
      </s.containerButtons>
    </s.Container>
  );
}

export default AddRoomModal;
